import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import {
	IconArrowLeftOutline18 as ArrowLeft,
	IconBellOutline18 as Bell,
	IconChevronLeftOutline18 as ChevronLeft,
	IconChevronRightOutline18 as ChevronRight,
	IconDotsOutline18 as Dots,
	IconHashtagOutline18 as Hash,
	IconInboxOutline18 as InboxIcon,
	IconLayersStackedOutline18 as LayersStacked,
	IconLockOutline18 as Lock,
	IconStackPerspectiveOutline18 as StackPerspective,
	IconPlusOutline18 as Plus,
	IconArrowRotateClockwiseOutline18 as Refresh,
	IconMagnifierOutline18 as Search,
	IconTagOutline18 as Tag,
	IconUserOutline18 as User,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Avatar,
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuItem,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	IconButton,
	Input,
	IntegrationTemplate,
	Select,
	Switch,
} from "@productlane/ui"
import { SlackIcon } from "@productlane/ui/src/icons/slack-logo"

import type { NavItem } from "@/screens/app/sidebar"
import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { IntegrationLayout } from "../../settings/integrations/integration-layout"
import {
	DEFAULT_GROUPING,
	GROUPING_OPTIONS,
} from "../../settings/integrations/slack-grouping"
import { SettingsHeader } from "../../settings/settings-header"
import {
	NAV_ROW_ICON_GAP_CLASS,
	NAV_ROW_ICON_SIZE_CLASS,
	NAV_ROW_PADDING_LEFT_CLASS,
	NAV_ROW_VERTICAL_PADDING_CLASS,
} from "../../settings/settings-nav-geometry"
import { HowItWorksButton } from "../../settings/settings-switches/how-it-works-button"
import { SettingItem } from "../../settings/settings-switches/setting-item"
import {
	SETTINGS_PAGE_PADDING_X_CLASS,
	SettingsCardInsetContext,
	SettingsContainer,
} from "../../settings/settings-switches/settings-container"
import {
	ACCOUNT_NAV_ITEMS,
	FEATURES_NAV_ITEMS,
	SETUP_NAV_ITEMS,
	WORKSPACE_NAV_ITEMS,
} from "../../settings/sidebar"
import { DesignIssueHeader } from "./design-issue-header"

/* ------------------------------------------------------------------ */
/* Design markers                                                      */
/* ------------------------------------------------------------------ */

type MarkKind = "New" | "Changed" | "Moved"

function Mark(_props: { kind: MarkKind }) {
	return null
}

function Marked({ children }: { children: ReactNode; kind: MarkKind }) {
	return <>{children}</>
}

/* ------------------------------------------------------------------ */
/* Settings shell replica (SettingsSidebar + SettingsLayout)           */
/* ------------------------------------------------------------------ */

function StaticNavRow({ item, active }: { item: NavItem; active: boolean }) {
	const Icon = item.icon
	return (
		<div
			className={cn(
				"group/navlink flex w-full items-center rounded-md pr-2",
				NAV_ROW_VERTICAL_PADDING_CLASS,
				NAV_ROW_PADDING_LEFT_CLASS,
				active
					? "bg-gray-150 dark:bg-gray-800"
					: "bg-transparent hover:bg-gray-150 hover:dark:bg-gray-800",
			)}
		>
			<Icon
				strokeWidth={1}
				className={cn(
					"flex flex-none items-center justify-center text-gray-800 group-hover/navlink:text-primary",
					NAV_ROW_ICON_GAP_CLASS,
					NAV_ROW_ICON_SIZE_CLASS,
					active ? "dark:text-gray-200" : "dark:text-gray-400",
				)}
			/>
			<div
				className={cn(
					"flex-1 select-none truncate text-left text-sm font-medium group-hover/navlink:text-primary",
					active ? "text-primary" : "text-secondary dark:text-gray-400",
				)}
			>
				{item.title}
			</div>
		</div>
	)
}

function NavGroup({ title, items }: { title: string; items: NavItem[] }) {
	return (
		<div className="mt-4 flex w-full flex-col gap-0.5 px-2.5 first:mt-0 first:pt-3">
			<div className="pb-1 pl-2 text-xs font-normal text-tertiary">{title}</div>
			{items.map((item) => (
				<StaticNavRow
					key={item.href}
					item={item}
					active={item.href === "/settings/integrations"}
				/>
			))}
		</div>
	)
}

function SettingsRail() {
	return (
		<div className="w-[15.1rem] flex-none border-r border-gray-200 bg-gray-100 pb-20 dark:border-gray-700/80 dark:bg-gray-950">
			<div className="w-full px-2.5 pb-4 pt-2.5">
				<div
					className={cn(
						"mb-2.5 flex w-full items-center rounded-md pr-2",
						NAV_ROW_PADDING_LEFT_CLASS,
						NAV_ROW_VERTICAL_PADDING_CLASS,
					)}
				>
					<ArrowLeft
						strokeWidth={1}
						className={cn(
							"flex-none text-tertiary",
							NAV_ROW_ICON_GAP_CLASS,
							NAV_ROW_ICON_SIZE_CLASS,
						)}
					/>
					<span className="flex-1 select-none truncate text-xs text-tertiary">
						Back to app
					</span>
				</div>
				<div className="flex h-7 items-center gap-2 rounded-md bg-white pl-2 text-sm text-tertiary ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-700/80">
					<Search strokeWidth={1} className="size-4 flex-none" />
					Search settings
				</div>
			</div>
			<NavGroup title="Administration" items={WORKSPACE_NAV_ITEMS} />
			<NavGroup title="Personal" items={ACCOUNT_NAV_ITEMS} />
			<NavGroup title="Setup" items={SETUP_NAV_ITEMS} />
			<NavGroup title="Features" items={FEATURES_NAV_ITEMS} />
		</div>
	)
}

function SettingsShell({
	path,
	backTo,
	children,
}: {
	path: string
	backTo: ReactNode
	children: ReactNode
}) {
	return (
		<div className="space-y-3">
			<div className="font-mono text-xs text-tertiary">{path}</div>
			<div className="rounded-2xl bg-gray-100 p-2 dark:bg-gray-950">
				<div
					className="flex overflow-hidden rounded-2xl bg-gray-25 shadow-md ring-1 ring-black/10 dark:bg-gray-900 dark:shadow-black dark:ring-white/15"
					translate="no"
				>
					<SettingsRail />
					<div className="flex min-w-0 flex-1 flex-col">
						<div className="block px-8 pt-4">{backTo}</div>
						<main
							className={cn(
								"mx-auto flex w-full max-w-[704px] shrink-0 grow cursor-default flex-col px-5 py-16",
								SETTINGS_PAGE_PADDING_X_CLASS,
							)}
						>
							<SettingsCardInsetContext.Provider value={true}>
								{children}
							</SettingsCardInsetContext.Provider>
						</main>
					</div>
				</div>
			</div>
		</div>
	)
}

/* ------------------------------------------------------------------ */
/* Mock data                                                           */
/* ------------------------------------------------------------------ */

const NEW_CHANNEL_OPTIONS = [
	{ value: "on", label: "Become inboxes" },
	{ value: "off", label: "Stay off until turned on" },
]

const WORKSPACE_DEFAULT = "default"

const CHANNEL_GROUPING_OPTIONS = [
	{ value: WORKSPACE_DEFAULT, label: "Workspace default" },
	...GROUPING_OPTIONS,
]

interface Channel {
	id: string
	name: string
	shared: boolean
	private: boolean
	threads: string
	tag?: string
	assignee?: string
	inbox: boolean
	changelog: boolean
	usedFor?: string
}

function channel(
	id: string,
	name: string,
	rest: Partial<Omit<Channel, "id" | "name">> = {},
): Channel {
	return {
		id,
		name,
		shared: true,
		private: true,
		threads: WORKSPACE_DEFAULT,
		inbox: false,
		changelog: false,
		...rest,
	}
}

const LINEAR_CHANNELS: Channel[] = [
	channel("1", "ext-acme", {
		threads: "SINGLE:60",
		tag: "Managed",
		assignee: "Dana Lee",
		inbox: true,
		changelog: true,
	}),
	channel("2", "ext-globex", {
		tag: "Managed",
		assignee: "Dana Lee",
		inbox: true,
		changelog: true,
	}),
	channel("3", "ext-hooli", { inbox: true }),
	channel("4", "ext-initech", { changelog: true }),
	channel("5", "ext-initrode", { changelog: true }),
	channel("6", "ext-soylent"),
	channel("7", "ext-umbrella", { private: false, changelog: true }),
	channel("8", "cx-alerts", {
		shared: false,
		usedFor: "Notifications",
	}),
	channel("9", "announcements", {
		shared: false,
		private: false,
		changelog: true,
	}),
	channel("10", "support-eu", { shared: false, private: false }),
]

const COMMUNITY_CHANNELS: Channel[] = [
	channel("c1", "help", { shared: false, private: false, inbox: true }),
	channel("c2", "bugs", { shared: false, private: false, inbox: true }),
	channel("c3", "feature-requests", {
		shared: false,
		private: false,
		inbox: true,
	}),
	channel("c4", "general", { shared: false, private: false }),
]

function notificationOptions(channels: Channel[]) {
	return [
		{ value: "__disable__", label: "Disable" },
		...[...channels]
			.sort((a, b) => a.name.localeCompare(b.name))
			.map((c) => ({ value: c.name, label: c.name })),
	]
}

interface SlackWorkspace {
	id: string
	name: string
	url: string
	channelSummary: string
	newChannels: string
	notificationChannel: string
	channels: Channel[]
}

const WORKSPACES: SlackWorkspace[] = [
	{
		id: "linear",
		name: "Linear",
		url: "linear.slack.com",
		channelSummary: "214 inbox channels, 786 others",
		newChannels: "off",
		notificationChannel: "cx-alerts",
		channels: LINEAR_CHANNELS,
	},
	{
		id: "linear-community",
		name: "Linear Community",
		url: "linear-community.slack.com",
		channelSummary: "3 inbox channels, 1 other",
		newChannels: "on",
		notificationChannel: "__disable__",
		channels: COMMUNITY_CHANNELS,
	},
]

/* ------------------------------------------------------------------ */
/* Shared pieces                                                       */
/* ------------------------------------------------------------------ */

function WorkspaceHeader({ workspace }: { workspace: SlackWorkspace }) {
	return (
		<div className="flex items-start gap-3">
			<Avatar
				name={workspace.name}
				roundness="sm"
				size="sm"
				className="flex-none"
			/>
			<div className="min-w-0 flex-1">
				<div className="text-sm font-medium text-primary">{workspace.name}</div>
				<div className="text-xs text-secondary">{workspace.url}</div>
			</div>
			<Mark kind="New" />
			<DropdownMenu
				trigger={<IconButton Icon={Dots} variant="ghost" />}
				contentProps={{ align: "end" }}
			>
				<DropdownMenuItem>Disconnect</DropdownMenuItem>
			</DropdownMenu>
		</div>
	)
}

/** The four workspace-wide settings every version keeps. */
function WorkspaceSettingItems({ workspace }: { workspace: SlackWorkspace }) {
	const [newChannels, setNewChannels] = useState(workspace.newChannels)
	const [grouping, setGrouping] = useState(DEFAULT_GROUPING)
	const [notificationChannelId, setNotificationChannelId] = useState(
		workspace.notificationChannel,
	)
	const [autoReplies, setAutoReplies] = useState(true)

	return (
		<>
			<SettingItem
				className="pt-4"
				title={<Marked kind="New">New channels</Marked>}
				description="What a channel does when the bot joins."
				action={
					<Select
						name="new-channels"
						value={newChannels}
						options={NEW_CHANNEL_OPTIONS}
						onChange={setNewChannels}
						className="w-48"
						selectClassName="dark:bg-gray-700/70"
					/>
				}
			/>
			<SettingItem
				className="pt-4"
				title={<Marked kind="Moved">Message grouping</Marked>}
				description="How long a channel's messages keep joining one thread."
				action={
					<Select
						name="grouping"
						value={grouping}
						options={GROUPING_OPTIONS}
						onChange={setGrouping}
						className="w-48"
						selectClassName="dark:bg-gray-700/70"
					/>
				}
			/>
			<SettingItem
				className="pt-4"
				title={<Marked kind="Moved">Notifications</Marked>}
				description="New threads will be sent to this channel."
				action={
					<Select
						name="notificationChannelId"
						value={notificationChannelId}
						options={notificationOptions(workspace.channels)}
						onChange={setNotificationChannelId}
						selectClassName="dark:bg-gray-700/70"
					/>
				}
			/>
			<SettingItem
				className="pt-4"
				title={<Marked kind="Moved">Thread auto-replies</Marked>}
				description="The bot automatically replies in Slack threads depending on your office hours and availability."
				action={
					<Switch
						name="pushBotReplyChannel"
						checked={autoReplies}
						onCheckedChange={setAutoReplies}
					/>
				}
			/>
		</>
	)
}

function PersonalNotificationsCard() {
	return (
		<SettingsContainer className="mt-4">
			<div className="space-y-4">
				<div className="text-sm">
					<div className="flex items-center justify-between">
						<div className="cursor-default">
							<p className="font-medium">Personal Slack notifications</p>
							<p className="mt-1 text-secondary">
								Set up personal Slack notifications for new assignments or
								comment mentions.
							</p>
						</div>
						<Button variant="default">Set up</Button>
					</div>
				</div>
			</div>
		</SettingsContainer>
	)
}

function SlackPageFrame({
	connectLabel,
	children,
}: {
	connectLabel: string
	children: ReactNode
}) {
	return (
		<>
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<SlackIcon className="w-10" />}
				name={
					<div className="flex flex-wrap items-center gap-2">
						Slack
						<Badge
							variant="green"
							size="sm"
							className="!text-green-700 dark:!text-green-400"
						>
							<span className="size-1.5 rounded-full bg-green-500" />
							Connected
						</Badge>
					</div>
				}
				description="Sync conversations from Slack."
				button={
					<div className="flex items-center gap-2">
						<HowItWorksButton
							article="eba421f3-5a25-48cc-88a3-2658544b24fa"
							variant="icon"
						/>
						<Button variant="outline" Icon={Plus}>
							{connectLabel}
						</Button>
						<Mark kind="Changed" />
					</div>
				}
				instructions={
					<>
						{children}
						<PersonalNotificationsCard />
					</>
				}
			/>
		</>
	)
}

function WorkspacesHeading() {
	return (
		<div className="flex items-center gap-2 text-sm font-medium">
			Workspaces
			<Mark kind="Changed" />
		</div>
	)
}

/* ------------------------------------------------------------------ */
/* Channel list pieces (approved: Linear-style rows, icon pills)       */
/* ------------------------------------------------------------------ */

type ChannelPatch = Partial<Pick<Channel, "inbox" | "changelog">>

function UsagePill({
	Icon,
	label,
	active = true,
	className,
}: {
	Icon?: typeof Bell
	label: string
	active?: boolean
	className?: string
}) {
	return (
		<span
			className={cn(
				"flex h-6 flex-none items-center gap-1 rounded-md px-1.5 text-xs font-medium",
				active
					? "bg-gray-100 text-primary dark:bg-gray-800"
					: "text-gray-300 dark:text-gray-600",
				className,
			)}
		>
			{Icon && (
				<Icon
					className={cn(
						"size-3.5 flex-none",
						active ? "text-secondary" : "text-gray-300 dark:text-gray-600",
					)}
				/>
			)}
			{label}
		</span>
	)
}

// Both slots always render at a fixed width, so the columns line up down the list.
function UsagePills({
	inbox,
	changelog,
}: {
	inbox: boolean
	changelog: boolean
}) {
	return (
		<>
			<UsagePill
				Icon={InboxIcon}
				label="Inbox"
				active={inbox}
				className="w-[68px]"
			/>
			<UsagePill
				Icon={StackPerspective}
				label="Changelog"
				active={changelog}
				className="w-[92px]"
			/>
		</>
	)
}

const PILL_TRIGGER_CLASS =
	"flex items-center gap-1 rounded-lg px-1 py-1 hover:bg-gray-100 dark:hover:bg-gray-800"

/** Version A and B: two independent toggles behind the pills. */
function ToggleUsageMenu({
	channel,
	onChange,
}: {
	channel: Channel
	onChange: (patch: ChannelPatch) => void
}) {
	return (
		<DropdownMenu
			trigger={
				<button type="button" className={PILL_TRIGGER_CLASS}>
					<UsagePills inbox={channel.inbox} changelog={channel.changelog} />
				</button>
			}
			contentProps={{ align: "end" }}
		>
			<DropdownMenuCheckboxItem
				checked={channel.inbox}
				onCheckedChange={(inbox) => onChange({ inbox })}
				onSelect={(e) => e.preventDefault()}
			>
				<span className="flex items-center gap-2">
					<InboxIcon className="size-4 flex-none text-secondary" />
					Inbox
				</span>
			</DropdownMenuCheckboxItem>
			<DropdownMenuCheckboxItem
				checked={channel.changelog}
				onCheckedChange={(changelog) => onChange({ changelog })}
				onSelect={(e) => e.preventDefault()}
			>
				<span className="flex items-center gap-2">
					<StackPerspective className="size-4 flex-none text-secondary" />
					Changelog
				</span>
			</DropdownMenuCheckboxItem>
		</DropdownMenu>
	)
}

type UseFor = "inbox" | "changelog" | "both" | "off"

const USE_FOR_PATCH: Record<UseFor, Required<ChannelPatch>> = {
	inbox: { inbox: true, changelog: false },
	changelog: { inbox: false, changelog: true },
	both: { inbox: true, changelog: true },
	off: { inbox: false, changelog: false },
}

function useForOf(c: Channel): UseFor {
	if (c.inbox && c.changelog) return "both"
	if (c.inbox) return "inbox"
	if (c.changelog) return "changelog"
	return "off"
}

const USE_FOR_OPTIONS: { value: UseFor; label: string; Icon?: typeof Bell }[] =
	[
		{ value: "inbox", label: "Inbox", Icon: InboxIcon },
		{ value: "changelog", label: "Changelog", Icon: StackPerspective },
		{ value: "both", label: "Inbox and changelog" },
		{ value: "off", label: "Off" },
	]

/** Version C: one choice per channel, so the four states are explicit. */
function UseForMenu({
	channel,
	onChange,
}: {
	channel: Channel
	onChange: (patch: ChannelPatch) => void
}) {
	return (
		<DropdownMenu
			trigger={
				<button type="button" className={PILL_TRIGGER_CLASS}>
					<UsagePills inbox={channel.inbox} changelog={channel.changelog} />
				</button>
			}
			contentProps={{ align: "end" }}
		>
			<div className="px-2 pb-1 pt-1.5 text-xs text-tertiary">Use for</div>
			<DropdownMenuRadioGroup
				value={useForOf(channel)}
				onValueChange={(v) => onChange(USE_FOR_PATCH[v as UseFor])}
			>
				{USE_FOR_OPTIONS.map((o) => (
					<DropdownMenuRadioItem key={o.value} value={o.value}>
						{o.label}
					</DropdownMenuRadioItem>
				))}
			</DropdownMenuRadioGroup>
		</DropdownMenu>
	)
}

function ChannelRow({
	channel,
	onChange,
	usage = "toggles",
	dense = false,
}: {
	channel: Channel
	onChange: (patch: ChannelPatch) => void
	usage?: "toggles" | "useFor"
	dense?: boolean
}) {
	const grouping = CHANNEL_GROUPING_OPTIONS.find(
		(o) => o.value === channel.threads,
	)
	return (
		<div
			className={cn(
				"group flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-850",
				dense ? "h-10 px-3" : "h-11 px-4",
			)}
		>
			{channel.private ? (
				<Lock className="size-3.5 flex-none text-tertiary" />
			) : (
				<Hash className="size-3.5 flex-none text-tertiary" />
			)}
			<span className="truncate text-sm text-primary">{channel.name}</span>
			{channel.shared && (
				<span className="flex-none text-xs text-tertiary">Shared</span>
			)}
			<div className="flex-1" />
			<div className="flex flex-none items-center gap-3 text-xs text-secondary">
				{channel.threads !== WORKSPACE_DEFAULT && grouping && (
					<span className="flex items-center gap-1">
						<LayersStacked className="size-3.5 flex-none text-tertiary" />
						{grouping.label}
					</span>
				)}
				{channel.tag && (
					<span className="flex items-center gap-1.5">
						<span className="size-1.5 flex-none rounded-full bg-violet-500" />
						{channel.tag}
					</span>
				)}
				{channel.assignee && (
					<Avatar name={channel.assignee} size="xs" className="flex-none" />
				)}
			</div>
			<div className="flex w-52 flex-none justify-end">
				{channel.usedFor ? (
					<div className="flex items-center gap-1 px-1 py-1">
						<UsagePill
							Icon={Bell}
							label={channel.usedFor}
							className="w-[164px] bg-transparent text-secondary dark:bg-transparent"
						/>
					</div>
				) : usage === "useFor" ? (
					<UseForMenu channel={channel} onChange={onChange} />
				) : (
					<ToggleUsageMenu channel={channel} onChange={onChange} />
				)}
			</div>
			<DropdownMenu
				trigger={
					<IconButton
						Icon={Dots}
						variant="ghost"
						className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
					/>
				}
				contentProps={{ align: "end" }}
			>
				<DropdownMenuItem Icon={LayersStacked}>
					Message grouping
				</DropdownMenuItem>
				<DropdownMenuItem Icon={Tag}>Default tag</DropdownMenuItem>
				<DropdownMenuItem Icon={User}>Default assignee</DropdownMenuItem>
			</DropdownMenu>
		</div>
	)
}

function SetAllMenu({
	count,
	onSet,
}: {
	count: number
	onSet: (patch: ChannelPatch) => void
}) {
	return (
		<DropdownMenu
			trigger={
				<button
					type="button"
					className="rounded-md px-2 py-1 text-xs font-medium text-secondary hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800"
				>
					Set all {count}
				</button>
			}
			contentProps={{ align: "end" }}
		>
			<DropdownMenuItem
				Icon={InboxIcon}
				onSelect={() => onSet({ inbox: true })}
			>
				Turn inbox on
			</DropdownMenuItem>
			<DropdownMenuItem
				Icon={InboxIcon}
				onSelect={() => onSet({ inbox: false })}
			>
				Turn inbox off
			</DropdownMenuItem>
			<DropdownMenuItem
				Icon={StackPerspective}
				onSelect={() => onSet({ changelog: true })}
			>
				Turn changelog on
			</DropdownMenuItem>
			<DropdownMenuItem
				Icon={StackPerspective}
				onSelect={() => onSet({ changelog: false })}
			>
				Turn changelog off
			</DropdownMenuItem>
		</DropdownMenu>
	)
}

function ChannelGroup({
	title,
	channels,
	onChange,
	dense,
}: {
	title: string
	channels: Channel[]
	onChange: (ids: string[], patch: ChannelPatch) => void
	dense?: boolean
}) {
	if (channels.length === 0) return null
	// Bulk edits skip channels that serve another purpose, like notifications.
	const editable = channels.filter((c) => !c.usedFor).map((c) => c.id)
	return (
		<div>
			<div
				className={cn(
					"flex h-9 items-center gap-2 bg-gray-50 text-xs font-medium text-secondary dark:bg-gray-850",
					dense ? "px-3" : "px-4",
				)}
			>
				{title}
				<span className="text-tertiary">{channels.length}</span>
				<div className="flex-1" />
				<SetAllMenu
					count={channels.length}
					onSet={(patch) => onChange(editable, patch)}
				/>
			</div>
			{channels.map((c) => (
				<ChannelRow
					key={c.id}
					channel={c}
					dense={dense}
					onChange={(patch) => onChange([c.id], patch)}
				/>
			))}
		</div>
	)
}

function useChannelState(initial: Channel[]) {
	const [channels, setChannels] = useState(initial)
	const [query, setQuery] = useState("")
	const visible = useMemo(() => {
		const q = query.trim().toLowerCase()
		return q
			? channels.filter((c) => c.name.toLowerCase().includes(q))
			: channels
	}, [channels, query])
	const update = (ids: string[], patch: ChannelPatch) =>
		setChannels((prev) =>
			prev.map((c) => (ids.includes(c.id) ? { ...c, ...patch } : c)),
		)
	return { visible, query, setQuery, update }
}

function NoMatch({ query }: { query: string }) {
	return (
		<div className="px-4 py-6 text-center text-sm text-tertiary">
			No channels match &quot;{query.trim()}&quot;
		</div>
	)
}

/** Version A and B: rows grouped into Inbox / Not an inbox, Set all per group. */
function GroupedChannelList({
	initial,
	dense = false,
}: {
	initial: Channel[]
	dense?: boolean
}) {
	const { visible, query, setQuery, update } = useChannelState(initial)
	const groups = (
		<>
			<ChannelGroup
				title="Inbox"
				channels={visible.filter((c) => c.inbox)}
				onChange={update}
				dense={dense}
			/>
			<ChannelGroup
				title="Not an inbox"
				channels={visible.filter((c) => !c.inbox)}
				onChange={update}
				dense={dense}
			/>
			{visible.length === 0 && <NoMatch query={query} />}
		</>
	)
	return (
		<div className="space-y-3">
			<div className="flex items-center gap-2">
				<Input
					name="search"
					icon={Search}
					placeholder="Search channels"
					className={dense ? "w-56" : "w-64"}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
				/>
				<div className="flex-1" />
				<IconButton Icon={Refresh} variant="ghost" title="Refresh from Slack" />
			</div>
			{dense ? (
				<div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700/80">
					<div className="max-h-80 overflow-y-auto pb-2">{groups}</div>
				</div>
			) : (
				<SettingsContainer className="space-y-0 divide-y-0 overflow-hidden p-0">
					{groups}
				</SettingsContainer>
			)}
		</div>
	)
}

/* ------------------------------------------------------------------ */
/* Version A: workspace cards, channels on a sub-page                  */
/* ------------------------------------------------------------------ */

function VersionAWorkspaceCard({ workspace }: { workspace: SlackWorkspace }) {
	return (
		<SettingsContainer>
			<WorkspaceHeader workspace={workspace} />
			<WorkspaceSettingItems workspace={workspace} />
			<div>
				<SettingItem
					className="pt-4"
					title={<Marked kind="New">Channels</Marked>}
					description={workspace.channelSummary}
					link="#slack-channels"
				/>
			</div>
		</SettingsContainer>
	)
}

function VersionASlackPage() {
	return (
		<SlackPageFrame connectLabel="Connect workspace">
			<div className="space-y-4">
				<WorkspacesHeading />
				{WORKSPACES.map((w) => (
					<VersionAWorkspaceCard key={w.id} workspace={w} />
				))}
			</div>
		</SlackPageFrame>
	)
}

function VersionAChannelsPage() {
	return (
		<>
			<SettingsHeader
				title="Channels"
				titleSuffix={<Mark kind="New" />}
				subtitle="Linear workspace, linear.slack.com"
			/>
			<GroupedChannelList initial={LINEAR_CHANNELS} />
		</>
	)
}

/* ------------------------------------------------------------------ */
/* Version B: channel list inline in each workspace card               */
/* ------------------------------------------------------------------ */

function VersionBWorkspaceCard({ workspace }: { workspace: SlackWorkspace }) {
	return (
		<SettingsContainer>
			<WorkspaceHeader workspace={workspace} />
			<WorkspaceSettingItems workspace={workspace} />
			<div className="space-y-3 pt-6">
				<div className="flex items-center gap-2">
					<Hash className="size-4 flex-none text-secondary" />
					<span className="text-sm font-medium text-primary">Channels</span>
					<Mark kind="New" />
					<span className="text-xs text-tertiary">
						{workspace.channelSummary}
					</span>
				</div>
				<GroupedChannelList initial={workspace.channels} dense />
			</div>
		</SettingsContainer>
	)
}

function VersionBSlackPage() {
	return (
		<SlackPageFrame connectLabel="Connect workspace">
			<div className="space-y-4">
				<WorkspacesHeading />
				{WORKSPACES.map((w) => (
					<VersionBWorkspaceCard key={w.id} workspace={w} />
				))}
			</div>
		</SlackPageFrame>
	)
}

/* ------------------------------------------------------------------ */
/* Version C: one row per workspace, one page per workspace            */
/* ------------------------------------------------------------------ */

function VersionCSlackPage() {
	return (
		<SlackPageFrame connectLabel="Add workspace">
			<div className="space-y-4">
				<WorkspacesHeading />
				<SettingsContainer className="divide-y divide-gray-200 p-0 dark:divide-gray-700/80">
					{WORKSPACES.map((w) => (
						<a
							key={w.id}
							href="#slack-channels"
							className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-850"
						>
							<Avatar
								name={w.name}
								roundness="sm"
								size="sm"
								className="flex-none"
							/>
							<div className="min-w-0 flex-1">
								<div className="text-sm font-medium text-primary">{w.name}</div>
								<div className="text-xs text-secondary">{w.url}</div>
							</div>
							<span className="flex-none text-xs text-secondary">
								{w.channelSummary}
							</span>
							<ChevronRight className="size-4 flex-none text-tertiary" />
						</a>
					))}
				</SettingsContainer>
			</div>
		</SlackPageFrame>
	)
}

function BulkBar({
	query,
	count,
	onSet,
}: {
	query: string
	count: number
	onSet: (useFor: UseFor) => void
}) {
	const searching = query.trim().length > 0
	return (
		<div className="flex h-11 items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 text-xs dark:border-gray-700/80 dark:bg-gray-850">
			{searching ? (
				<>
					<span className="font-medium text-primary">
						Set {count} matching {count === 1 ? "channel" : "channels"} to
					</span>
					<div className="flex-1" />
					{USE_FOR_OPTIONS.map((o) => (
						<Button
							key={o.value}
							variant="outline"
							size="sm"
							Icon={o.Icon}
							disabled={count === 0}
							onClick={() => onSet(o.value)}
						>
							{o.value === "both" ? "Both" : o.label}
						</Button>
					))}
				</>
			) : (
				<span className="text-tertiary">
					Search to change many channels at once, for example &quot;ext-&quot;.
				</span>
			)}
		</div>
	)
}

function VersionCChannelList({ initial }: { initial: Channel[] }) {
	const { visible, query, setQuery, update } = useChannelState(initial)
	const editable = visible.filter((c) => !c.usedFor)
	return (
		<div className="space-y-3">
			<div className="flex items-center gap-2">
				<Input
					name="search"
					icon={Search}
					placeholder="Search channels"
					className="w-64"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
				/>
				<div className="flex-1" />
				<IconButton Icon={Refresh} variant="ghost" title="Refresh from Slack" />
			</div>
			<SettingsContainer className="space-y-0 divide-y-0 overflow-hidden p-0">
				<BulkBar
					query={query}
					count={editable.length}
					onSet={(u) =>
						update(
							editable.map((c) => c.id),
							USE_FOR_PATCH[u],
						)
					}
				/>
				{visible.map((c) => (
					<ChannelRow
						key={c.id}
						channel={c}
						usage="useFor"
						onChange={(patch) => update([c.id], patch)}
					/>
				))}
				{visible.length === 0 && <NoMatch query={query} />}
			</SettingsContainer>
		</div>
	)
}

function VersionCWorkspacePage() {
	const workspace = WORKSPACES[0]!
	return (
		<>
			<SettingsHeader
				title={workspace.name}
				titleSuffix={<Mark kind="New" />}
				subtitle={workspace.url}
				action={
					<DropdownMenu
						trigger={<IconButton Icon={Dots} variant="ghost" />}
						contentProps={{ align: "end" }}
					>
						<DropdownMenuItem>Disconnect</DropdownMenuItem>
					</DropdownMenu>
				}
			/>
			<SettingsContainer>
				<div className="-mt-4">
					<WorkspaceSettingItems workspace={workspace} />
				</div>
			</SettingsContainer>
			<div className="mt-10 space-y-3">
				<div className="flex items-center gap-2 text-sm font-medium">
					Channels
					<Mark kind="New" />
				</div>
				<VersionCChannelList initial={workspace.channels} />
			</div>
		</>
	)
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

function BackLink({ label }: { label: string }) {
	return (
		<Button variant="ghost" Icon={ChevronLeft}>
			{label}
		</Button>
	)
}

const SLACK_PATH =
	"Settings / Integrations / Slack  (/settings/integrations/slack)"

function VersionColumn({
	label,
	note,
	children,
}: {
	label: string
	note: string
	children: ReactNode
}) {
	return (
		<div className="w-[1000px] flex-none space-y-10">
			<div className="space-y-1">
				<div className="text-base font-semibold text-primary">{label}</div>
				<div className="text-sm text-secondary">{note}</div>
			</div>
			{children}
		</div>
	)
}

function NoSubPage() {
	return (
		<div className="space-y-3">
			<div className="font-mono text-xs text-tertiary">
				Settings / Integrations / Slack / Linear / Channels
			</div>
			<div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-tertiary dark:border-gray-700/80">
				No channel sub-page in this version. Each workspace card holds its own
				list.
			</div>
		</div>
	)
}

export function SlackWorkspacesPage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<div className="min-h-screen space-y-12 bg-gray-50 px-10 py-10 pb-24 dark:bg-gray-950">
			<div className="max-w-[1200px]">
				<DesignIssueHeader
					identifier="PRO-10805"
					title="Connect several Slack channels and workspaces as support inboxes, with per-channel settings"
					url="https://linear.app/productlane/issue/PRO-10805"
					summary="Linear wants to move off Pylon, but they run support in two Slack workspaces and today we connect only one. This design lets you connect several Slack workspaces and set each channel on its own: whether it creates threads, how it groups messages, its default tag and assignee, and whether it gets changelog posts. It also covers RAP-152 and FEA-186, so a channel can get notifications or changelogs without filling the inbox. Green New marks what PRO-10805 adds, amber Changed or Moved marks what it alters. Everything unmarked matches production. Muted channels goes away: turning a channel's inbox off on the Channels page does the same job. Three versions sit side by side; read each column top to bottom."
				/>
			</div>
			<div className="flex flex-nowrap items-start gap-10 overflow-x-auto pb-4">
				<VersionColumn
					label="Version A"
					note="A card per workspace with its settings; channels open on their own page with Set all per group."
				>
					<SettingsShell
						path={SLACK_PATH}
						backTo={<BackLink label="Integrations" />}
					>
						<VersionASlackPage />
					</SettingsShell>
					<div id="slack-channels">
						<SettingsShell
							path="Settings / Integrations / Slack / Linear / Channels  (/settings/integrations/slack/linear/channels)"
							backTo={<BackLink label="Slack" />}
						>
							<VersionAChannelsPage />
						</SettingsShell>
					</div>
				</VersionColumn>
				<VersionColumn
					label="Version B"
					note="Everything on one page: each workspace card holds its own searchable channel list."
				>
					<SettingsShell
						path={SLACK_PATH}
						backTo={<BackLink label="Integrations" />}
					>
						<VersionBSlackPage />
					</SettingsShell>
					<NoSubPage />
				</VersionColumn>
				<VersionColumn
					label="Version C"
					note="A short list of workspaces; each opens one page with its settings and channels, one Use for choice per channel."
				>
					<SettingsShell
						path={SLACK_PATH}
						backTo={<BackLink label="Integrations" />}
					>
						<VersionCSlackPage />
					</SettingsShell>
					<SettingsShell
						path="Settings / Integrations / Slack / Linear  (/settings/integrations/slack/linear)"
						backTo={<BackLink label="Slack" />}
					>
						<VersionCWorkspacePage />
					</SettingsShell>
				</VersionColumn>
			</div>
		</div>
	)
}
